import L from "leaflet";
import { gpx as toGeoJSONgpx } from "@tmcw/togeojson";

const TRACK_STYLE = { color: "#d81b60", weight: 4, opacity: 0.95 };
const MAX_GPX_BYTES = 10 * 1024 * 1024;

export async function loadGpxFromFile(file, map) {
  if (!file) return null;

  const gpxText = await readFileAsText(file);
  return loadGpxFromText(gpxText, map);
}

export async function loadGpxFromUrl(urlValue, map) {
  const url = normalizeGpxUrl(urlValue);
  const response = await fetch(url.href, { credentials: "same-origin" });

  if (!response.ok) {
    throw new Error(`Unable to fetch GPX file (${response.status}).`);
  }

  const contentLength = response.headers.get("Content-Length");
  if (contentLength && Number(contentLength) > MAX_GPX_BYTES) {
    throw new Error("GPX file is too large.");
  }

  const gpxText = await response.text();
  if (gpxText.length > MAX_GPX_BYTES) {
    throw new Error("GPX file is too large.");
  }

  return loadGpxFromText(gpxText, map);
}

function loadGpxFromText(gpxText, map) {
  const xml = new DOMParser().parseFromString(gpxText, "application/xml");
  const parserError = xml.querySelector("parsererror");
  if (parserError) {
    throw new Error("Invalid GPX XML.");
  }

  const geoJson = toGeoJSONgpx(xml);

  const layer = L.geoJSON(geoJson, {
    style: (feature) =>
      feature.geometry?.type === "LineString" ? TRACK_STYLE : undefined,
    pointToLayer: (_, latlng) =>
      L.circleMarker(latlng, {
        radius: 4,
        weight: 2,
        opacity: 1,
        fillOpacity: 0.85,
      }),
  }).addTo(map);

  return {
    layer,
    bounds: layer.getBounds(),
  };
}

function normalizeGpxUrl(urlValue) {
  if (!urlValue || urlValue.length > 2048) {
    throw new Error("Invalid GPX URL.");
  }

  let url;
  try {
    url = new URL(urlValue, window.location.origin);
  } catch (e) {
    throw new Error("Invalid GPX URL.");
  }

  if (url.origin !== window.location.origin) {
    throw new Error("Only GPX files from this site can be loaded.");
  }

  if (!["http:", "https:"].includes(url.protocol)) {
    throw new Error("Invalid GPX URL protocol.");
  }

  if (!url.pathname.toLowerCase().endsWith(".gpx")) {
    throw new Error("The URL must point to a .gpx file.");
  }

  return url;
}

function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsText(file);
  });
}
